import { IAttribute } from "../../constants/product-customizations/customization";
import {
    CustomizationSelectionMap,
    TCustomizationSelection,
} from "constants/product-customizations/customization-selection.types";

import { TClosedRange } from "constants/product-customizations/types";
import _, { Dictionary } from "lodash";
import { IProduct } from "../../records/item/item";
import { BrandRecord } from "../../records/brand";

export const idMapping = <T extends { _id: string }>(array: T[], key = "_id"): Dictionary<T> => _.keyBy(array, key);

export const deleteAttribute = (attributes: IAttribute[], id: string): IAttribute[] => {
    const attributesMap = idMapping(attributes, "id");
    if (!attributesMap[id]) {
        return attributes;
    }

    delete attributesMap[id];

    return Object.values(attributesMap);
};

export const reorderSelectedValues = (
    orderedAttributes: IAttribute[],
    selectedValues: CustomizationSelectionMap
): CustomizationSelectionMap =>
    orderedAttributes.reduce((result, { _id: id }) => {
        const selectedValue = selectedValues.get(id);
        if (selectedValue) {
            result.set(id, selectedValue);
        }

        return result;
    }, new CustomizationSelectionMap());

export const mutateAttributes = (state?: IAttribute[]) => (previousState?: IAttribute[]) => {
    const current = state ?? [];
    const previous = previousState ?? [];

    const length = current.length;
    if (length != previous.length) {
        return state;
    }

    for (let index = 0; index < length; index += 1) {
        if (current[index]._id != previous[index]._id) {
            return state;
        }
    }

    return previousState;
};

export const getSelection = (values: CustomizationSelectionMap): TCustomizationSelection[] =>
    Array.from(values.values()).filter(Boolean);

export const deleteAttributeById = (id: string) => (attributes: IAttribute[]) => deleteAttribute(attributes, id);

export type TCustomizationDropdownState = {
    requiredAttributes: IAttribute[];
    optionalAttributes: IAttribute[];
    optionalAttributeLimit: TClosedRange | undefined;
};

const dropdownState = ({
    requiredAttributes = [],
    optionalAttributes = [],
    optionalAttributeLimit,
}: Partial<TCustomizationDropdownState>): TCustomizationDropdownState => ({
    requiredAttributes,
    optionalAttributes,
    optionalAttributeLimit,
});

export const getDropdownState = (
    attributes?: IAttribute[],
    attributeLimit?: TClosedRange
): TCustomizationDropdownState => {
    if (!attributeLimit || !attributes) {
        return dropdownState({ requiredAttributes: attributes });
    }

    const optionalAttributes: IAttribute[] = [];
    const requiredAttributes: IAttribute[] = [];
    attributes.forEach((attribute) => {
        const attributesToPush = attribute.isRequired ? requiredAttributes : optionalAttributes;
        attributesToPush.push(attribute);
    });

    const optionalLength = optionalAttributes.length;
    const requiredLength = requiredAttributes.length;
    if (attributes.length === 1 && optionalLength === 1) {
        return dropdownState({ requiredAttributes: optionalAttributes });
    }

    return dropdownState({
        requiredAttributes,
        optionalAttributes,
        optionalAttributeLimit: !optionalLength
            ? undefined
            : {
                  min: attributeLimit.min - requiredLength,
                  max: attributeLimit.max - requiredLength,
              },
    });
};

export const getImageUrl = (cdn: string, type: "product" | "brands", name: string): string =>
    `${cdn}/${type}/${name}.jpg`;

export const getImageSource = (brand: BrandRecord | null, product: IProduct, defaultImage: string = ""): string => {
    const { __images, cdn, __cdn, __media } = product ?? ({} as IProduct);
    const cdnUrl = cdn ?? __cdn;
    const missingLogoImage = brand?.settings?.missing_logo_image;

    if (defaultImage) {
        return defaultImage;
    }

    if (brand?.getPreference("unlimited_images") && __media?.length > 0) {
        return __media?.[0]?.url;
    }

    if (cdnUrl && __images?.length > 0) {
        return getImageUrl(cdnUrl, "product", __images?.[0]);
    }

    if (cdnUrl && missingLogoImage) {
        return getImageUrl(cdnUrl, "brands", missingLogoImage);
    }

    return "";
};
